import React, { useState, useEffect, useRef } from 'react';
import { MdBuild, MdDevices, MdMeetingRoom, MdSchedule, MdWarning, MdCheckCircle, MdTrendingUp, MdRefresh, MdBusiness, MdInventory, MdShoppingCart, MdForum, MdArrowForward, MdEvent } from 'react-icons/md';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../i18n';

const PIE_COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function Dashboard() {
    const { isManager, assignedFacility, user } = useAuth();
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const refreshing = useRef(false);

    const load = async () => {
        if (refreshing.current) return;
        refreshing.current = true;
        try {
            setError('');
            const { data: d } = await API.get('/dashboard/summary');
            setData(d);
        } catch (e) {
            console.error(e);
            setError(e.response?.data?.error || t('dashboard.loadError'));
        } finally {
            setLoading(false);
            refreshing.current = false;
        }
    };

    useEffect(() => {
        const refreshOnReturn = () => load();

        load();
        window.addEventListener('focus', refreshOnReturn);
        window.addEventListener('pageshow', refreshOnReturn);
        window.addEventListener('fms:data-changed', refreshOnReturn);
        const refreshTimer = window.setInterval(refreshOnReturn, 15000);
        return () => {
            window.removeEventListener('focus', refreshOnReturn);
            window.removeEventListener('pageshow', refreshOnReturn);
            window.removeEventListener('fms:data-changed', refreshOnReturn);
            window.clearInterval(refreshTimer);
        };
    }, []);

    if (loading) return (
        <div className="spinner-wrap">
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('dashboard.loading')}</span></div>
        </div>
    );

    if (error) return (
        <div className="page-card" style={{ padding: 24 }}>
            <h5 style={{ marginTop: 0 }}>{t('dashboard.unavailable')}</h5>
            <p style={{ color: '#64748b' }}>{error}</p>
            <button className="btn btn-primary btn-sm" onClick={load}>{t('dashboard.retry')}</button>
        </div>
    );

    const wo = data?.work_orders || {};
    const assets = data?.assets || {};
    const spaces = data?.spaces || {};
    const maint = data?.maintenance || {};
    const inventory = data?.inventory || {};
    const requests = data?.asset_requests || {};
    const statusLabel = status => t(`status.${String(status || '').replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())}`, String(status || '').replaceAll('_', ' '));
    const priorityLabel = priority => t(`priority.${priority}`, priority);

    // A single plain-English sentence that says whether anything needs
    // attention today, so someone who doesn't know what "critical priority"
    // or "overdue maintenance" means can still tell at a glance if action is
    // needed — without having to read and interpret every number below.
    const attentionCount = (wo.critical || 0) + (maint.overdue || 0) + (inventory.low_stock || 0) + (requests.pending || 0);
    const attentionMessage = attentionCount === 0
        ? t('dashboard.allClear')
        : t('dashboard.attentionMessage', '', { n: attentionCount });

    // Grouped, in-plain-words versions of the stat tiles below. Each group is
    // its own labeled section, and every number gets one short sentence
    // underneath explaining what it actually means — so someone who isn't a
    // facilities professional doesn't have to guess what "open orders" or
    // "low stock" refers to.
    const statGroups = [
        {
            title: t('dashboard.groupWorkOrdersTitle'), subtitle: t('dashboard.groupWorkOrdersSubtitle'),
            stats: [
                { icon: <MdBuild />, color: 'blue', value: wo.total || 0, label: t('dashboard.totalWorkOrders'), caption: t('dashboard.captionTotalWO') },
                { icon: <MdBuild />, color: 'yellow', value: wo.open || 0, label: t('dashboard.openOrders'), caption: t('dashboard.captionOpenWO') },
                { icon: <MdWarning />, color: 'red', value: wo.critical || 0, label: t('dashboard.criticalPriority'), caption: t('dashboard.captionCriticalWO') },
            ]
        },
        {
            title: t('dashboard.groupFacilityTitle'), subtitle: t('dashboard.groupFacilitySubtitle'),
            stats: [
                { icon: <MdDevices />, color: 'green', value: assets.total || 0, label: t('dashboard.totalAssets'), caption: t('dashboard.captionAssets') },
                { icon: <MdMeetingRoom />, color: 'teal', value: spaces.available || 0, label: t('dashboard.availableSpaces'), caption: t('dashboard.captionSpaces') },
                { icon: <MdSchedule />, color: 'red', value: maint.overdue || 0, label: t('dashboard.overdueMaintenance'), caption: t('dashboard.captionMaintenance') },
            ]
        },
        {
            title: t('dashboard.groupInventoryTitle'), subtitle: t('dashboard.groupInventorySubtitle'),
            stats: [
                { icon: <MdInventory />, color: 'yellow', value: inventory.low_stock || 0, label: t('dashboard.lowStock'), caption: t('dashboard.captionLowStock') },
                { icon: <MdShoppingCart />, color: 'blue', value: requests.pending || 0, label: t('dashboard.assetRequests'), caption: t('dashboard.captionRequests') },
                { icon: <MdForum />, color: 'teal', value: data?.unread_notifications || 0, label: t('dashboard.unreadAlerts'), caption: t('dashboard.captionAlerts') },
            ]
        },
    ];

    const woStatusData = [
        { name: t('status.open'), value: wo.open || 0 },
        { name: t('status.inProgress'), value: wo.in_progress || 0 },
        { name: t('status.completed'), value: wo.completed || 0 },
    ];

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>{t('nav.dashboard')}</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {isManager && assignedFacility
                            ? <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <MdBusiness style={{ color: '#0ea5e9' }} />
                                {assignedFacility.name} - {t('dashboard.facilityOverview')}
                            </span>
                            : t('dashboard.overview')}
                    </p>
                </div>
                <button className="btn btn-light btn-sm" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdRefresh size={16} /> {t('dashboard.refresh')}
                </button>
            </div>

            <div style={{ background: 'linear-gradient(110deg, #0d2343, #17558d)', color: '#fff', borderRadius: 14, padding: '22px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                <div><div style={{ fontSize: 13, color: '#b9d8ff', marginBottom: 4 }}>{t('dashboard.greeting')} {user.name}</div><h5 style={{ margin: 0, fontWeight: 700 }}>{attentionMessage}</h5></div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <a href="/app/work-orders" className="btn btn-light btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><MdBuild /> {t('nav.workOrders')} <MdArrowForward /></a>
                    <a href="/app/messages" className="btn btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#fff', border: '1px solid rgba(255,255,255,.35)' }}><MdForum /> {t('nav.messages')}</a>
                </div>
            </div>

            {/* Stats — grouped into labeled sections, each number with a
                plain-English sentence underneath explaining what it means. */}
            {statGroups.map(group => (
                <div key={group.title} style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{group.title}</span>
                        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>{group.subtitle}</span>
                    </div>
                    <div className="row g-3">
                        {group.stats.map(s => (
                            <div className="col-lg-4 col-md-6 col-12" key={s.label}>
                                <div className="stat-card" style={{ alignItems: 'flex-start' }}>
                                    <div className={`stat-icon ${s.color}`}>{s.icon}</div>
                                    <div>
                                        <div className="stat-value">{s.value}</div>
                                        <div className="stat-label">{s.label}</div>
                                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3, lineHeight: 1.4 }}>{s.caption}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Charts Row */}
            <div className="row g-3 mb-4">
                <div className="col-lg-8">
                    <div className="page-card">
                        <div className="page-card-header">
                            <h6><MdTrendingUp style={{ marginRight: 8, color: '#0ea5e9' }} />{t('dashboard.workOrderTrend')}</h6>
                        </div>
                        <div style={{ padding: '16px' }}>
                            <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px' }}>{t('dashboard.trendCaption')}</p>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={data?.monthly_trend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="created" name={t('dashboard.created')} fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="completed" name={t('dashboard.completed')} fill="#22c55e" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="page-card">
                        <div className="page-card-header">
                            <h6>{t('dashboard.workOrderStatus')}</h6>
                        </div>
                        <div style={{ padding: '16px' }}>
                            <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 12px' }}>{t('dashboard.statusCaption')}</p>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={woStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={80}
                                        paddingAngle={3} dataKey="value">
                                        {woStatusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tables Row */}
            <div className="row g-3">
                <div className="col-lg-7">
                    <div className="page-card">
                        <div className="page-card-header">
                            <h6>{t('dashboard.recentWorkOrders')}</h6>
                            <a href="/app/work-orders" className="btn btn-sm btn-light" style={{ fontSize: 12 }}>{t('dashboard.viewAll')}</a>
                        </div>
                        <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 16px 0' }}>{t('dashboard.recentCaption')}</p>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="fms-table">
                                <thead>
                                    <tr>
                                        <th>{t('dashboard.workOrderNumber')}</th><th>{t('dashboard.title')}</th><th>{t('dashboard.priority')}</th><th>{t('dashboard.status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.recent_work_orders || []).map(wo => (
                                        <tr key={wo.id}>
                                            <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0ea5e9' }}>{wo.wo_number}</span></td>
                                            <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wo.title}</td>
                                            <td><span className={`badge-priority ${wo.priority}`}>{priorityLabel(wo.priority)}</span></td>
                                            <td><span className={`badge-status ${wo.status}`}>{statusLabel(wo.status)}</span></td>
                                        </tr>
                                    ))}
                                    {(!data?.recent_work_orders?.length) && (
                                        <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>{t('dashboard.noWorkOrders')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-lg-5">
                    <div className="page-card">
                        <div className="page-card-header">
                            <h6><MdSchedule style={{ marginRight: 6, color: '#f59e0b' }} />{t('dashboard.upcomingMaintenance')}</h6>
                        </div>
                        <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 16px 0' }}>{t('dashboard.upcomingMaintCaption')}</p>
                        <div style={{ padding: '4px 0' }}>
                            {(data?.upcoming_maintenance || []).map((item, i) => (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    padding: '10px 16px', borderBottom: '1px solid #f1f5f9'
                                }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                                        background: item.days_remaining <= 0 ? '#fee2e2' : item.days_remaining <= 7 ? '#fef3c7' : '#e0f2fe',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 11, fontWeight: 700,
                                        color: item.days_remaining <= 0 ? '#b91c1c' : item.days_remaining <= 7 ? '#b45309' : '#0369a1'
                                    }}>
                                        {item.days_remaining <= 0 ? t('dashboard.overdue') : `${item.days_remaining} ${t('dashboard.days')}`}
                                    </div>
                                    <div style={{ overflow: 'hidden' }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>{item.asset_name || t('dashboard.general')}</div>
                                    </div>
                                    <div style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{item.next_due}</div>
                                </div>
                            ))}
                            {(!data?.upcoming_maintenance?.length) && (
                                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                                    <MdCheckCircle size={32} style={{ color: '#22c55e', marginBottom: 8 }} /><br />
                                    {t('dashboard.noUpcomingMaintenance')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-3 mt-1">
                <div className="col-lg-7">
                    <div className="page-card">
                        <div className="page-card-header"><h6><MdShoppingCart style={{ marginRight: 8, color: '#0ea5e9' }} />{t('dashboard.assetRequests')}</h6><a href="/app/work-orders" className="btn btn-sm btn-light">{t('dashboard.viewAll')} <MdArrowForward /></a></div>
                        <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 16px 0' }}>{t('dashboard.requestsCaption')}</p>
                        <div style={{ overflowX: 'auto' }}><table className="fms-table"><thead><tr><th>{t('dashboard.request')}</th><th>{t('dashboard.requester')}</th><th>{t('dashboard.status')}</th><th>{t('dashboard.date')}</th></tr></thead><tbody>{(data?.recent_asset_requests || []).map(item => <tr key={item.id}><td><strong>{item.asset_name}</strong><div style={{ fontSize: 11, color: '#94a3b8' }}>{item.wo_number} - {t('dashboard.quantity')} {item.quantity}</div></td><td>{item.requester_name || '—'}</td><td><span className={`badge-status ${item.status}`}>{statusLabel(item.status)}</span></td><td style={{ fontSize: 12 }}>{new Date(item.created_at).toLocaleDateString()}</td></tr>)}{!data?.recent_asset_requests?.length && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>{t('dashboard.noAssetRequests')}</td></tr>}</tbody></table></div>
                    </div>
                </div>
                <div className="col-lg-5">
                    <div className="page-card"><div className="page-card-header"><h6><MdEvent style={{ marginRight: 8, color: '#0ea5e9' }} />{t('dashboard.upcomingBookings')}</h6><a href="/app/bookings" className="btn btn-sm btn-light">{t('dashboard.viewAll')}</a></div><p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 16px 0' }}>{t('dashboard.bookingsCaption')}</p><div style={{ padding: '4px 0' }}>{(data?.upcoming_bookings || []).map(booking => <div key={booking.id} style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}><div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><strong style={{ fontSize: 13 }}>{booking.title || t('dashboard.untitledBooking')}</strong><span style={{ fontSize: 11, color: '#64748b' }}>{new Date(booking.start_datetime).toLocaleDateString()}</span></div><div style={{ fontSize: 12, color: '#64748b' }}>{booking.space_name} - {booking.building_name}</div></div>)}{!data?.upcoming_bookings?.length && <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>{t('dashboard.noUpcomingBookings')}</div>}</div></div>
                </div>
            </div>
        </div>
    );
}
